import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmDialog from '../common/ConfirmDialog';
import TableSkeleton from '../common/TableSkeleton';
import { useAuth } from '../../hooks/useAuth';
import { berechneArbeitstage, endeNachArbeitstagen, feiertageImZeitraum } from '../../utils/arbeitstageBerechnung';

export interface AbwesenheitEintragBase {
  id: number;
  von: string;
  bis: string;
  anzahl_tage: number;
  status: string;
  bemerkung?: string;
  admin_bemerkung?: string;
  mitarbeiter_id: number;
  mitarbeiter?: { vorname: string; nachname: string };
}

interface FormData {
  von: string;
  bis: string;
  anzahl_tage: number;
  bemerkung: string;
}

interface AbwesenheitTypPanelProps {
  typLabel: string;
  hinweis?: string;
  showResttageHinweis?: boolean;
  editableStatuses: string[];
  deletableStatuses: string[];
  canViewAll: boolean;
  canApprove?: boolean;
  loadEigene: (jahr: number) => Promise<AbwesenheitEintragBase[]>;
  loadAlle?: (jahr: number) => Promise<AbwesenheitEintragBase[]>;
  create: (data: FormData) => Promise<unknown>;
  remove: (id: number) => Promise<void>;
  genehmigen?: (id: number, bemerkung?: string) => Promise<unknown>;
  ablehnen?: (id: number, bemerkung?: string) => Promise<unknown>;
  createButtonLabel: string;
}

function rundeAufHalbeSchritte(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.max(0.5, Math.round(n * 2) / 2);
}

function ersterSinnvollerArbeitstagAbHeute(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = 0; i < 400; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const key = `${y}-${m}-${day}`;
    if (berechneArbeitstage(key, key) >= 1) return key;
    d.setDate(d.getDate() + 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (status === 'Genehmigt' || status === 'Gemeldet') return 'success';
  if (status === 'Beantragt') return 'warning';
  if (status === 'Abgelehnt') return 'error';
  return 'default';
};

const AbwesenheitTypPanel: React.FC<AbwesenheitTypPanelProps> = ({
  typLabel,
  hinweis,
  editableStatuses,
  deletableStatuses,
  canViewAll,
  canApprove = false,
  loadEigene,
  loadAlle,
  create,
  remove,
  genehmigen,
  ablehnen,
  createButtonLabel,
}) => {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eigene, setEigene] = useState<AbwesenheitEintragBase[]>([]);
  const [alle, setAlle] = useState<AbwesenheitEintragBase[]>([]);
  const [listTab, setListTab] = useState<'eigene' | 'alle'>('eigene');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ von: '', bis: '', anzahl_tage: 0, bemerkung: '' });
  const [feiertagInfo, setFeiertagInfo] = useState<Array<{ datum: string; name: string }>>([]);
  const [deleteTarget, setDeleteTarget] = useState<AbwesenheitEintragBase | null>(null);
  const [adminDialog, setAdminDialog] = useState<{
    open: boolean;
    action: 'genehmigen' | 'ablehnen';
    item: AbwesenheitEintragBase | null;
  }>({ open: false, action: 'genehmigen', item: null });
  const [adminBemerkung, setAdminBemerkung] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meine = await loadEigene(currentYear);
      setEigene(meine);
      if (canViewAll && loadAlle) {
        setAlle(await loadAlle(currentYear));
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [canViewAll, currentYear, loadAlle, loadEigene]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!dialogOpen) return;
    const { von, bis } = formData;
    if (von && bis && bis >= von) setFeiertagInfo(feiertageImZeitraum(von, bis));
    else setFeiertagInfo([]);
  }, [dialogOpen, formData.von, formData.bis]);

  const openCreate = () => {
    const start = ersterSinnvollerArbeitstagAbHeute();
    setFormData({
      von: start,
      bis: start,
      anzahl_tage: berechneArbeitstage(start, start),
      bemerkung: '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!formData.von || !formData.bis) {
      setFormError('Bitte Von- und Bis-Datum angeben');
      return;
    }
    if (formData.bis < formData.von) {
      setFormError('Das Enddatum darf nicht vor dem Startdatum liegen');
      return;
    }
    try {
      await create(formData);
      setDialogOpen(false);
      await loadData();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setFormError(ax.response?.data?.detail || (err instanceof Error ? err.message : 'Fehler'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
      setDeleteTarget(null);
    }
  };

  const handleAdmin = async () => {
    if (!adminDialog.item) return;
    try {
      if (adminDialog.action === 'genehmigen' && genehmigen) {
        await genehmigen(adminDialog.item.id, adminBemerkung || undefined);
      } else if (adminDialog.action === 'ablehnen' && ablehnen) {
        await ablehnen(adminDialog.item.id, adminBemerkung || undefined);
      }
      setAdminDialog({ open: false, action: 'genehmigen', item: null });
      setAdminBemerkung('');
      await loadData();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail || (err instanceof Error ? err.message : 'Aktion fehlgeschlagen'));
    }
  };

  const rows = listTab === 'alle' && canViewAll ? alle : eigene;

  return (
    <Box>
      {hinweis && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {hinweis}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={listTab === 'eigene' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setListTab('eigene')}
          >
            Meine {typLabel}
          </Button>
          {canViewAll && (
            <Button
              variant={listTab === 'alle' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setListTab('alle')}
            >
              Alle
            </Button>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {createButtonLabel}
        </Button>
      </Box>

      <TableContainer>
        {loading ? (
          <TableSkeleton columns={listTab === 'alle' ? 7 : 6} rows={5} />
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Keine Einträge für {currentYear}</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {listTab === 'alle' && <TableCell>Mitarbeiter</TableCell>}
                <TableCell>Von</TableCell>
                <TableCell>Bis</TableCell>
                <TableCell>Tage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Bemerkung</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} hover>
                  {listTab === 'alle' && (
                    <TableCell>
                      {u.mitarbeiter
                        ? `${u.mitarbeiter.vorname} ${u.mitarbeiter.nachname}`
                        : `ID ${u.mitarbeiter_id}`}
                    </TableCell>
                  )}
                  <TableCell>{formatDate(u.von)}</TableCell>
                  <TableCell>{formatDate(u.bis)}</TableCell>
                  <TableCell>{u.anzahl_tage}</TableCell>
                  <TableCell>
                    <Chip size="small" label={u.status} color={statusColor(u.status)} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.bemerkung || '-'}
                  </TableCell>
                  <TableCell align="right">
                    {listTab === 'eigene' && deletableStatuses.includes(u.status) && (
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(u)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {listTab === 'alle' &&
                      canApprove &&
                      u.status === 'Beantragt' &&
                      u.mitarbeiter_id !== user?.id && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Genehmigen">
                            <IconButton
                              size="small"
                              sx={{ color: '#1b5e20' }}
                              onClick={() =>
                                setAdminDialog({ open: true, action: 'genehmigen', item: u })
                              }
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ablehnen">
                            <IconButton
                              size="small"
                              sx={{ color: '#b71c1c' }}
                              onClick={() =>
                                setAdminDialog({ open: true, action: 'ablehnen', item: u })
                              }
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{createButtonLabel}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Von"
            type="date"
            value={formData.von}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => {
                if (!value) return { ...prev, von: '', bis: '', anzahl_tage: 0 };
                let nextBis = prev.bis;
                if (!nextBis || nextBis < value) nextBis = value;
                return { ...prev, von: value, bis: nextBis, anzahl_tage: berechneArbeitstage(value, nextBis) };
              });
            }}
          />
          <TextField
            label="Bis"
            type="date"
            value={formData.bis}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => {
                if (!value || !prev.von) return { ...prev, bis: value };
                if (value < prev.von) {
                  return { ...prev, bis: prev.von, anzahl_tage: berechneArbeitstage(prev.von, prev.von) };
                }
                return { ...prev, bis: value, anzahl_tage: berechneArbeitstage(prev.von, value) };
              });
            }}
          />
          <TextField
            label="Anzahl Tage"
            type="number"
            value={formData.anzahl_tage}
            inputProps={{ step: 0.5, min: 0.5 }}
            onChange={(e) => {
              const n = parseFloat(e.target.value.replace(',', '.'));
              if (Number.isNaN(n) || !formData.von) return;
              const anzahl = rundeAufHalbeSchritte(n);
              const bis = endeNachArbeitstagen(formData.von, anzahl);
              setFormData((prev) => ({ ...prev, anzahl_tage: anzahl, bis: bis || prev.bis }));
            }}
          />
          {feiertagInfo.length > 0 && (
            <Alert severity="info">
              Feiertage im Zeitraum: {feiertagInfo.map((f) => f.name).join(', ')}
            </Alert>
          )}
          <TextField
            label="Bemerkung"
            multiline
            minRows={2}
            value={formData.bemerkung}
            onChange={(e) => setFormData((prev) => ({ ...prev, bemerkung: e.target.value }))}
            helperText={
              typLabel === 'Krankheit'
                ? 'Bitte keine Diagnosen oder Gesundheitsdetails eintragen'
                : undefined
            }
          />
          {!editableStatuses.length && null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Eintrag löschen?"
        message="Möchten Sie diesen Eintrag wirklich löschen?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Dialog
        open={adminDialog.open}
        onClose={() => setAdminDialog({ open: false, action: 'genehmigen', item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {adminDialog.action === 'genehmigen' ? 'Genehmigen' : 'Ablehnen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Admin-Bemerkung (optional)"
            fullWidth
            multiline
            minRows={2}
            value={adminBemerkung}
            onChange={(e) => setAdminBemerkung(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialog({ open: false, action: 'genehmigen', item: null })}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={adminDialog.action === 'ablehnen' ? 'error' : 'primary'}
            onClick={handleAdmin}
          >
            {adminDialog.action === 'genehmigen' ? 'Genehmigen' : 'Ablehnen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AbwesenheitTypPanel;
